/** Literal reference query of docs/08 §6 (whole workspace). Slow over PowerSync views; tests only. */
export const REFERENCE_QUERY = `
WITH RECURSIVE eff(block_id, tag_id, inherited_from) AS (
  SELECT b.id, bt.tag_id, CASE WHEN bt.tag_id IS NULL THEN NULL ELSE b.id END
  FROM blocks b
  LEFT JOIN block_tags bt ON bt.block_id = b.id AND bt.taxonomy_id = ?
  WHERE b.workspace_id = ? AND b.parent_id IS NULL AND b.deleted_at IS NULL
  UNION ALL
  SELECT c.id,
         COALESCE(bt.tag_id, eff.tag_id),
         CASE WHEN bt.tag_id IS NOT NULL THEN c.id ELSE eff.inherited_from END
  FROM blocks c
  JOIN eff ON c.parent_id = eff.block_id
  LEFT JOIN block_tags bt ON bt.block_id = c.id AND bt.taxonomy_id = ?
  WHERE c.deleted_at IS NULL
)
SELECT block_id, tag_id, inherited_from FROM eff`;
/** Same recursion over the TEMP table `own_tags(block_id, tag_id)` of one taxonomy. */
const ALL_QUERY = `
WITH RECURSIVE eff(block_id, tag_id, inherited_from) AS (
  SELECT b.id, ot.tag_id, CASE WHEN ot.tag_id IS NULL THEN NULL ELSE b.id END
  FROM blocks b
  LEFT JOIN own_tags ot ON ot.block_id = b.id
  WHERE b.workspace_id = ? AND b.parent_id IS NULL AND b.deleted_at IS NULL
  UNION ALL
  SELECT c.id,
         COALESCE(ot.tag_id, eff.tag_id),
         CASE WHEN ot.tag_id IS NOT NULL THEN c.id ELSE eff.inherited_from END
  FROM blocks c
  JOIN eff ON c.parent_id = eff.block_id
  LEFT JOIN own_tags ot ON ot.block_id = c.id
  WHERE c.deleted_at IS NULL
)
SELECT block_id, tag_id, inherited_from FROM eff`;
/** Recursion rooted at one block, seeded with what its parent passes down. */
const SUBTREE_QUERY = `
WITH RECURSIVE eff(block_id, tag_id, inherited_from) AS (
  SELECT b.id,
         COALESCE(ot.tag_id, ?),
         CASE WHEN ot.tag_id IS NOT NULL THEN b.id ELSE ? END
  FROM blocks b
  LEFT JOIN own_tags ot ON ot.block_id = b.id
  WHERE b.id = ? AND b.deleted_at IS NULL
  UNION ALL
  SELECT c.id,
         COALESCE(ot.tag_id, eff.tag_id),
         CASE WHEN ot.tag_id IS NOT NULL THEN c.id ELSE eff.inherited_from END
  FROM blocks c
  JOIN eff ON c.parent_id = eff.block_id
  LEFT JOIN own_tags ot ON ot.block_id = c.id
  WHERE c.deleted_at IS NULL
)
SELECT block_id, tag_id, inherited_from FROM eff`;
const SELECT_ROWS = 'SELECT block_id, tag_id, inherited_from FROM eff';
const INSERT_PREFIX = 'INSERT INTO effective_tags (id, block_id, taxonomy_id, tag_id, inherited_from)';
const INSERT_SELECT = "SELECT block_id || ':' || ?, block_id, ?, tag_id, inherited_from FROM eff";
async function withOwnTags(tx, taxonomyId, fn) {
    await tx.execute('CREATE TEMP TABLE IF NOT EXISTS own_tags (block_id TEXT PRIMARY KEY, tag_id TEXT)');
    await tx.execute('DELETE FROM own_tags');
    await tx.execute('INSERT OR REPLACE INTO own_tags (block_id, tag_id) SELECT block_id, tag_id FROM block_tags WHERE taxonomy_id = ?', [taxonomyId]);
    try {
        return await fn();
    }
    finally {
        await tx.execute('DROP TABLE IF EXISTS own_tags');
    }
}
/**
 * Rows the reference query yields for the workspace. `literal: true` runs the docs/08 §6 SQL
 * as written (slow over PowerSync views, fine for small fixtures); otherwise the indexed form.
 */
export async function referenceEffectiveTags(db, scope, { literal = false } = {}) {
    if (literal) {
        const rows = await db.getAll(REFERENCE_QUERY, [
            scope.taxonomyId,
            scope.workspaceId,
            scope.taxonomyId,
        ]);
        return rows.map((r) => ({ ...r, taxonomy_id: scope.taxonomyId }));
    }
    return db.writeTransaction((tx) => withOwnTags(tx, scope.taxonomyId, async () => {
        const rows = await tx.getAll(ALL_QUERY, [
            scope.workspaceId,
        ]);
        return rows.map((r) => ({ ...r, taxonomy_id: scope.taxonomyId }));
    }));
}
/** Current content of the local table for the taxonomy. */
export async function readEffectiveTags(db, taxonomyId) {
    return db.getAll('SELECT block_id, taxonomy_id, tag_id, inherited_from FROM effective_tags WHERE taxonomy_id = ?', [taxonomyId]);
}
/** Full recalculation for one taxonomy of a workspace, in one transaction. Returns row count. */
export async function rebuildAll(db, scope) {
    return db.writeTransaction(async (tx) => {
        await tx.execute('DELETE FROM effective_tags WHERE taxonomy_id = ?', [scope.taxonomyId]);
        await withOwnTags(tx, scope.taxonomyId, () => tx.execute(`${INSERT_PREFIX} ${ALL_QUERY.replace(SELECT_ROWS, INSERT_SELECT)}`, [
            scope.workspaceId,
            scope.taxonomyId,
            scope.taxonomyId,
        ]));
        const row = await tx.get('SELECT count(*) AS n FROM effective_tags WHERE taxonomy_id = ?', [scope.taxonomyId]);
        return row.n;
    });
}
/**
 * Recalculates the subtree rooted at `rootBlockId` (the block whose parent or own tag changed).
 * The root inherits from its parent's effective tag; rows of the subtree are replaced.
 * Returns the number of rows rewritten.
 */
export async function rebuildSubtree(db, scope, rootBlockId) {
    return db.writeTransaction(async (tx) => {
        const parent = await tx.getOptional(`SELECT et.tag_id, et.inherited_from FROM blocks b
       JOIN effective_tags et ON et.block_id = b.parent_id AND et.taxonomy_id = ?
       WHERE b.id = ?`, [scope.taxonomyId, rootBlockId]);
        const subtree = await tx.getAll(`WITH RECURSIVE sub(block_id) AS (
         SELECT id FROM blocks WHERE id = ?
         UNION ALL
         SELECT c.id FROM blocks c JOIN sub ON c.parent_id = sub.block_id
       ) SELECT block_id FROM sub`, [rootBlockId]);
        for (const row of subtree) {
            await tx.execute('DELETE FROM effective_tags WHERE id = ?', [
                `${row.block_id}:${scope.taxonomyId}`,
            ]);
        }
        await withOwnTags(tx, scope.taxonomyId, () => tx.execute(`${INSERT_PREFIX} ${SUBTREE_QUERY.replace(SELECT_ROWS, INSERT_SELECT)}`, [
            parent?.tag_id ?? null,
            parent?.inherited_from ?? null,
            rootBlockId,
            scope.taxonomyId,
            scope.taxonomyId,
        ]));
        return subtree.length;
    });
}
//# sourceMappingURL=effective-tags.js.map